import React, { useState, useEffect } from "react"
import { Form, Typography, Row, Col, Spin } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import { serialize } from "object-to-formdata"
import { ListingActionButtons } from "layoutsV2/pro/ProNewListingLayout/ListingActionButtons"
import AddStaffModal from "pagesV2/pro/Staff/AddStaffModal"
import { useResourceData } from "./hooks/useResourceData"
import { useEquipmentManagement } from "./hooks/useEquipmentManagement"
import { useCrewMemberManagement } from "./hooks/useCrewMemberManagement"
import { EquipmentSection } from "./components/EquipmentSection"
import { CrewMemberSection } from "./components/CrewMemberSection"
import { updateTrip, getGuideTrip } from "store/trips"
import { setFormTouched, setFormValues } from "store/proNewListingForms"
import { toaster } from "components/common/Toast"
import { modelSelector } from "store/selectors"

const { Title, Text } = Typography

export const NewListingResources = () => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const dispatch = useDispatch()
  const { id } = useParams()
  const [isLoading, setIsLoading] = useState(false)
  const [autoAssignEquipment, setAutoAssignEquipment] = useState(false)
  const trip = useSelector(modelSelector("trip"))

  // Custom hooks for data and state management
  const { equipmentData, crewMemberData, categoryOptions, roleOptions } = useResourceData()

  const {
    selectedEquipment,
    setSelectedEquipment,
    selectedCategories,
    setSelectedCategories,
    selectedEquipmentData,
    handleEditEquipment,
    handleRemoveEquipment,
    handleOpenModal
  } = useEquipmentManagement(equipmentData)

  const {
    selectedCrewMembers,
    setSelectedCrewMembers,
    selectedCrewMembersData,
    selectedRoles,
    setSelectedRoles,
    isAddStaffModalVisible,
    editingCrewMember,
    handleEditCrewMember,
    handleRemoveCrewMember,
    handleOpenStaffModal,
    handleCloseStaffModal
  } = useCrewMemberManagement(crewMemberData)

  // Fetch trip data on mount
  useEffect(() => {
    if (id) {
      dispatch(getGuideTrip(id))
    }
  }, [dispatch, id])

  // Initialize selected equipment and crew from trip data
  useEffect(() => {
    if (trip?.boat_ids) {
      setSelectedEquipment(trip.boat_ids.map(String))
    }
    if (trip?.assigned_staff_ids) {
      setSelectedCrewMembers(trip.assigned_staff_ids.map(String))
    }
    if (trip?.auto_assign_equipment !== undefined) {
      setAutoAssignEquipment(trip.auto_assign_equipment)
    }
  }, [trip, setSelectedEquipment, setSelectedCrewMembers])

  const handleValuesChange = (changedValues, allValues) => {
    dispatch(setFormValues("resources", allValues))
    dispatch(setFormTouched("resources", true))
  }

  const onFinish = (callback) => {
    setIsLoading(true)
    const formData = serialize(
      {
        trip: {
          boat_ids: selectedEquipment.filter((id) => id !== "all").map(Number),
          assigned_staff_ids: selectedCrewMembers.filter((id) => id !== "all").map(Number),
          auto_assign_equipment: autoAssignEquipment
        }
      },
      { allowEmptyArrays: true }
    )

    dispatch(updateTrip(id, formData))
      .then(() => {
        dispatch(setFormTouched("resources", true))
        setIsLoading(false)
        callback?.()
      })
      .catch(() => {
        setIsLoading(false)
      })
  }

  return (
    <Spin spinning={isLoading} size="large" style={{ width: "100%" }}>
      <Form form={form} className="d-flex flex-column flex-grow-1" onValuesChange={handleValuesChange}>
        <Title level={3} className="mb-0">
          {t("listings.tabs.resources.title")}
        </Title>
        <Text type="primary" className="text-lg">
          {t("listings.tabs.resources.new_subtitle")}
        </Text>

        <Row gutter={[16, 16]} className="mt-4">
          <Col lg={12}>
            <EquipmentSection
              equipmentData={equipmentData}
              selectedEquipment={selectedEquipment}
              onEquipmentChange={setSelectedEquipment}
              selectedCategories={selectedCategories}
              onCategoryChange={setSelectedCategories}
              categoryOptions={categoryOptions}
              selectedEquipmentData={selectedEquipmentData}
              onEdit={handleEditEquipment}
              onRemove={handleRemoveEquipment}
              onAddClick={handleOpenModal}
              autoAssignEquipment={autoAssignEquipment}
              onAutoAssignChange={setAutoAssignEquipment}
            />
          </Col>

          <Col lg={12}>
            <CrewMemberSection
              crewMemberData={crewMemberData}
              selectedCrewMembers={selectedCrewMembers}
              onCrewMemberChange={setSelectedCrewMembers}
              selectedRoles={selectedRoles}
              onRoleChange={setSelectedRoles}
              roleOptions={roleOptions}
              selectedCrewMembersData={selectedCrewMembersData}
              onEdit={handleEditCrewMember}
              onRemove={handleRemoveCrewMember}
              onAddClick={handleOpenStaffModal}
            />
          </Col>
        </Row>

        <ListingActionButtons form={form} onFinish={onFinish} />

        <AddStaffModal staffId={editingCrewMember?.id} editMode={!!editingCrewMember?.id} open={isAddStaffModalVisible} onCancel={handleCloseStaffModal} memberType="crew" />
      </Form>
    </Spin>
  )
}
